import React, { useState, useEffect } from "react";
import { Menu, Phone, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="9455" className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-semibold text-slate-600 transition-colors hover:text-blue-600">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-blue-600 transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-10 w-full bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block rounded-md px-4 py-3 text-lg font-semibold text-slate-900 hover:bg-slate-50 hover:text-blue-600 transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="primary-navigation" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b", isScrolled ? "bg-white/80 backdrop-blur-[12px] border-slate-100 shadow-md" : "bg-white border-transparent shadow-none")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="flex items-center">
            <img src="https://learning-marmoset.10web.cloud/wp-content/uploads/2026/03/FocusPath-Logo-Small.png" alt="FocusPath Consulting" className="h-8 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex md:flex-1 md:justify-center">
          <MenuProvider menu_id="90" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          <Button asChild className="hidden md:inline-flex bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md transition-transform hover:scale-105">
            <Link to="/ContactPage" className="flex items-center gap-2">
              <Phone className="size-4 text-white" />
              <span className="tracking-tight text-sm">
                Schedule a Consultation
              </span>
            </Link>
          </Button>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-slate-900 hover:bg-slate-50" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-2xl md:hidden animate-in slide-in-from-top-5 fade-in duration-300">
          <MenuProvider menu_id="90" className="w-full">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          <div className="p-4 pt-2 pb-6">
            <Button asChild className="w-full justify-center bg-blue-600 text-white h-12 text-lg">
              <Link to="/ContactPage" className="flex items-center gap-2">
                <Phone className="size-5 text-white" />
                <span className="font-semibold">Schedule a Consultation</span>
              </Link>
            </Button>
          </div>
        </div>}
    </header>;
}